/**
 * CapiEventQualityScoreMonitoringPage — Meta CAPI event quality score monitor report.
 * Aggregates snapshots by event_name: avg match_quality_score, avg match_rate, total event_count,
 * and latest snapshot timestamp. Numbers-only table. GET /api/v1/capi-event-quality-score-monitoring.
 */
import { useMemo } from "react";
import { useCapiEventQualityScoreMonitoringList } from "./hooks";
import type { CapiEventQualityScoreMonitoringRow } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";

interface EventBreakdownRow {
	event_name: string;
	avg_score: number;
	avg_match_rate: number | null;
	total_events: number;
	snapshot_count: number;
	latest_at: number;
}

function aggregate(items: CapiEventQualityScoreMonitoringRow[]): EventBreakdownRow[] {
	const byEvent = new Map<
		string,
		{
			score_sum: number;
			score_n: number;
			rate_sum: number;
			rate_n: number;
			total_events: number;
			snapshot_count: number;
			latest_at: number;
		}
	>();
	for (const row of items) {
		const agg = byEvent.get(row.event_name) ?? {
			score_sum: 0,
			score_n: 0,
			rate_sum: 0,
			rate_n: 0,
			total_events: 0,
			snapshot_count: 0,
			latest_at: 0,
		};
		agg.score_sum += row.match_quality_score;
		agg.score_n += 1;
		if (row.match_rate !== null && row.match_rate !== undefined) {
			agg.rate_sum += row.match_rate;
			agg.rate_n += 1;
		}
		agg.total_events += row.event_count;
		agg.snapshot_count += 1;
		if (row.created_at > agg.latest_at) agg.latest_at = row.created_at;
		byEvent.set(row.event_name, agg);
	}
	return Array.from(byEvent.entries())
		.map(([event_name, a]) => ({
			event_name,
			avg_score: a.score_n > 0 ? a.score_sum / a.score_n : 0,
			avg_match_rate: a.rate_n > 0 ? a.rate_sum / a.rate_n : null,
			total_events: a.total_events,
			snapshot_count: a.snapshot_count,
			latest_at: a.latest_at,
		}))
		.sort((a, b) => b.avg_score - a.avg_score);
}

function fmtScore(n: number): string {
	return n.toFixed(2);
}

function fmtRate(n: number | null): string {
	if (n === null) return "—";
	return `${(n * 100).toFixed(1)}%`;
}

function fmtDate(unix: number): string {
	if (!unix) return "—";
	return new Date(unix * 1000).toISOString().slice(0, 16).replace("T", " ");
}

export function CapiEventQualityScoreMonitoringPage() {
	const listQuery = useCapiEventQualityScoreMonitoringList();
	const items = listQuery.data?.items ?? [];
	const rollup = useMemo(() => aggregate(items), [items]);

	const overallAvg = useMemo(() => {
		if (items.length === 0) return 0;
		return items.reduce((s, r) => s + r.match_quality_score, 0) / items.length;
	}, [items]);
	const totalEvents = useMemo(() => items.reduce((s, r) => s + r.event_count, 0), [items]);

	return (
		<main
			data-testid="capi-event-quality-score-monitoring-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Report
				</p>
				<h1 className="bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Meta CAPI Event Quality Score
				</h1>
				<p className="text-sm text-zinc-600">
					Per-event match quality snapshots reported by Meta&apos;s Events Manager.
				</p>
			</header>

			<section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				<div className={CARD} data-testid="capi-event-quality-score-monitoring-kpi-avg">
					<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
						Overall avg score
					</p>
					<p className="mt-1 text-2xl font-semibold tabular-nums text-green-700">
						{items.length > 0 ? fmtScore(overallAvg) : "—"}
						<span className="ml-1 text-sm font-normal text-zinc-500">/ 10</span>
					</p>
				</div>
				<div className={CARD} data-testid="capi-event-quality-score-monitoring-kpi-events">
					<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
						Total events
					</p>
					<p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
						{totalEvents.toLocaleString()}
					</p>
				</div>
				<div className={CARD} data-testid="capi-event-quality-score-monitoring-kpi-snapshots">
					<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
						Snapshots
					</p>
					<p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
						{items.length.toLocaleString()}
					</p>
				</div>
			</section>

			<section className={CARD} aria-label="Quality breakdown by event name">
				<div className="mb-3 flex items-center justify-between">
					<h2 className="text-sm font-semibold text-zinc-900">Breakdown by event name</h2>
					<p className="text-xs text-zinc-500">
						{rollup.length} event {rollup.length === 1 ? "type" : "types"}
					</p>
				</div>
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="capi-event-quality-score-monitoring-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : rollup.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="capi-event-quality-score-monitoring-empty"
					>
						No quality score snapshots yet.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="capi-event-quality-score-monitoring-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Event name</th>
									<th className={TH}>Avg score</th>
									<th className={TH}>Avg match rate</th>
									<th className={TH}>Total events</th>
									<th className={TH}>Snapshots</th>
									<th className={TH}>Latest</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{rollup.map((r) => (
									<tr
										key={r.event_name}
										data-testid={`capi-event-quality-score-monitoring-row-${r.event_name}`}
									>
										<td className={`${TD} font-medium`}>{r.event_name}</td>
										<td className={`${TD} tabular-nums text-green-700`}>
											{fmtScore(r.avg_score)}
										</td>
										<td className={`${TD} tabular-nums`}>{fmtRate(r.avg_match_rate)}</td>
										<td className={`${TD} tabular-nums`}>
											{r.total_events.toLocaleString()}
										</td>
										<td className={`${TD} tabular-nums`}>
											{r.snapshot_count.toLocaleString()}
										</td>
										<td className={`${TD} tabular-nums text-zinc-500`}>
											{fmtDate(r.latest_at)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default CapiEventQualityScoreMonitoringPage;
